import './ConfigLinks.scss';

import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import Picture from '@/common/components/atoms/Picture/Picture';
import LinkLikeButton from '@/common/components/blocks/LinkLikeButton/LinkLikeButton';
import ScrollList from '@/common/components/molecules/ScrollList/ScrollList';
import {
  PRODUCT_NAME_MAX_LENGTH,
  PRODUCTS_CONFIGURABLES_LIMIT,
  ROUTE_PRODUCT,
} from '@/common/constants';
import getImageBySize from '@/common/helpers/getImageBySize';
import RelatedLink from '@/ebat/components/blocks/Product/RelatedLink/RelatedLink';
import { PRODUCT_REFERENCES_FORMAT } from '@/ebat/constants';

import {
  ConfigLinksDefaultProps,
  ConfigLinksPropTypes,
} from './ConfigLinks.proptypes';

const ConfigLinks = ({
  className,
  configurations,
  bundleParents,
  isBundle,
  title,
}) => {
  const { t } = useTranslation();

  /**
   * Return product's name
   * @param {string} itemName
   * @type {string}
   */
  const getConfigurableName = (itemName) =>
    itemName.length > PRODUCT_NAME_MAX_LENGTH
      ? `${itemName.slice(0, PRODUCT_NAME_MAX_LENGTH)}…`
      : itemName;

  /**
   * Check if product has more than x configurations
   * @type {boolean}
   */
  const hasMoreConfigs = () =>
    configurations.length > PRODUCTS_CONFIGURABLES_LIMIT;

  /**
   * Take product and return image alt or product name
   * @param {object} item : product
   * @returns {string}
   */
  const getImageAlt = (item) => item.image?.alt || item.name;

  /**
   * Take product and return image src or empty string
   * @param {object} item : product
   * @returns {string}
   */
  const getImageSrc = (item) =>
    item.image?.url
      ? getImageBySize(item.image.url, PRODUCT_REFERENCES_FORMAT)
      : '';

  return (
    <div className={cn('config-links', className)} data-qa-id="config-links">
      <p className="title">{title}</p>
      {bundleParents?.length > 0 && (
        <LinkLikeButton
          className="config-links__bundle-parents"
          url={`${ROUTE_PRODUCT}/${bundleParents[0]?.id}/${bundleParents[0]?.slug}`}
        >
          {t('ebat:entities:product.details.bundle_parents.label')}
        </LinkLikeButton>
      )}
      {hasMoreConfigs() && bundleParents?.length === 0 ? (
        <RelatedLink
          anchor={isBundle ? '#related-products-list' : '#config-products-list'}
          label={
            isBundle
              ? t('ebat:entities:product.details.related.label', {
                  count: configurations.length,
                })
              : t('ebat:entities:product.details.references.label', {
                  count: configurations.length,
                })
          }
        />
      ) : (
        bundleParents?.length === 0 && (
          <ScrollList
            className={`list list--references${
              hasMoreConfigs() ? ' list--scrollable' : ''
            }`}
          >
            {configurations.map(
              (item, index) =>
                index < PRODUCTS_CONFIGURABLES_LIMIT && (
                  <li
                    className="list__item"
                    key={`product-reference-${item.id}`}
                    data-qa-item-id={item.id}
                    data-qa-id="list-item"
                  >
                    <NavLink
                      to={`${ROUTE_PRODUCT}/${item.path}/${item.slug}`}
                      data-qa-id="reference-link"
                      className="reference-link"
                    >
                      <div className="container">
                        <Picture
                          alt={getImageAlt(item)}
                          src={getImageSrc(item)}
                        />
                        <span className="label">
                          {getConfigurableName(item.name)}
                        </span>
                      </div>
                    </NavLink>
                  </li>
                ),
            )}
          </ScrollList>
        )
      )}
    </div>
  );
};

ConfigLinks.defaultProps = {
  ...ConfigLinksDefaultProps,
};

ConfigLinks.propTypes = {
  ...ConfigLinksPropTypes,
};

export default ConfigLinks;
